import { useState, useRef, useEffect, useCallback } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { api } from '../services/api'
import type { CortexChatMessage } from '../services/api'
import MarkdownMessage from '../components/MarkdownMessage'
import './ScoutLandingPage.css'
import { scopedLocalStorage, scopedSessionStorage } from '../lib/storage'

const SESSION_KEY = 'scout_public_session_id'

// TQ #7060: nur gueltige UUIDs als session_id verwenden. Aeltere Builds haben
// teils nicht-UUID Werte (z.B. "scout-xxx") im sessionStorage abgelegt; Cortex
// lehnt die mit 422 ab → portal-api meldete 502. Ungueltige Werte verwerfen.
const isUuid = (s: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)

// Liest die gespeicherte session_id, verwirft sie aber wenn kein gueltiges UUID.
const readStoredSessionId = (): string | undefined => {
  const stored = scopedSessionStorage.getItem(SESSION_KEY)
  if (stored && !isUuid(stored)) {
    scopedSessionStorage.removeItem(SESSION_KEY)
    return undefined
  }
  return stored ?? undefined
}

// TQ #6490 (Scout-Reliability FE): lokale Message-Erweiterung. `failed`
// markiert eine User-Message deren Antwort nicht ankam — sie bleibt sichtbar
// (nicht aus der Liste entfernt) + bekommt einen Retry-Button. Vorher loeschte
// der catch-Pfad die Message, was dem User signalisierte "nicht angekommen"
// und ihn zum Mehrfach-Tippen verleitete (Vorfall #6484: 4x dieselbe Frage).
type ScoutMsg = CortexChatMessage & { failed?: boolean }

export default function ScoutLandingPage() {
  const { login } = useAuth()

  const [messages, setMessages] = useState<ScoutMsg[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | undefined>(readStoredSessionId)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 140)}px`
    }
  }, [input])

  // TQ #7544: Chatverlauf persistent ueber Navigation. Erst SOFORT aus localStorage
  // (ueberlebt Zuruecknavigieren auch wenn die Server-History gerade nicht verfuegbar
  // ist), dann Server-History als Ergaenzung. Bei leerer/unbekannter Server-Session
  // NICHT mehr wipen — der lokale Verlauf bleibt sichtbar (Marco-Feedback 2026-06-10).
  useEffect(() => {
    if (!sessionId) return
    try {
      const cached = scopedLocalStorage.getItem(`scout_messages_${sessionId}`)
      if (cached) {
        const parsed = JSON.parse(cached)
        if (Array.isArray(parsed) && parsed.length) setMessages(parsed)
      }
    } catch {
      // beschaedigter Cache — ignorieren
    }
    let cancelled = false
    api
      .publicScoutHistory(sessionId)
      .then(history => {
        if (cancelled) return
        if (history.length > 0) setMessages(history)
        // leere/unbekannte Server-Session: lokalen Verlauf behalten, nicht wipen
      })
      .catch(err => {
        console.error('Scout history load failed:', err)
        // Don't surface the error to the user — they can just keep chatting.
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // TQ #7544: Verlauf nach jeder Aenderung in localStorage spiegeln (max 100),
  // damit er Navigation/Reload ueberlebt — unabhaengig von der Server-History.
  useEffect(() => {
    if (sessionId && messages.length) {
      try {
        scopedLocalStorage.setItem(`scout_messages_${sessionId}`, JSON.stringify(messages.slice(-100)))
      } catch {
        // localStorage-Quota — ignorieren
      }
    }
  }, [messages, sessionId])

  const handleNewChat = () => {
    if (sessionId) scopedLocalStorage.removeItem(`scout_messages_${sessionId}`)
    scopedSessionStorage.removeItem(SESSION_KEY)
    setSessionId(undefined)
    setMessages([])
    setError(null)
    setInput('')
  }

  // TQ #6490: zentrale Send-Routine. `retryContent` wird vom Retry-Button
  // genutzt (re-sendet eine zuvor fehlgeschlagene Message ohne sie zu
  // duplizieren). Bei Erfolg verschwindet das `failed`-Flag der Message.
  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!trimmed || loading) return

      setError(null)
      // User-Message anhaengen (ohne failed-Flag — wird bei catch gesetzt).
      // Falls dieselbe Message schon als failed in der Liste steht (Retry),
      // failed-Flag entfernen statt zu duplizieren.
      setMessages(prev => {
        const lastFailedIdx = [...prev]
          .reverse()
          .findIndex(m => m.role === 'user' && m.failed && m.content === trimmed)
        if (lastFailedIdx !== -1) {
          const idx = prev.length - 1 - lastFailedIdx
          return prev.map((m, i) => (i === idx ? { ...m, failed: false } : m))
        }
        return [...prev, { role: 'user', content: trimmed }]
      })

      try {
        setLoading(true)
        const response = await api.publicScoutChat(trimmed, sessionId)

        // session_id IMMER aus der Response uebernehmen wenn (noch) keine
        // lokale gesetzt ist — verhindert dass Folge-Sends mit sessionId=
        // undefined neue Sessions erzeugen (Vorfall #6484: 3 msgs / 2 sessions).
        if (!sessionId && response.session_id) {
          setSessionId(response.session_id)
          scopedSessionStorage.setItem(SESSION_KEY, response.session_id)
        }

        setMessages(prev => [...prev, { role: 'assistant', content: response.response }])
      } catch (err) {
        console.error('Scout chat error:', err)
        const e = err as { error?: string }
        if (e?.error?.includes('too many requests')) {
          setError('Zu viele Anfragen — bitte einen Moment warten.')
        } else if (e?.error?.includes('timeout') || e?.error?.includes('aborted')) {
          setError('Scout antwortet gerade nicht. Die Frage bleibt erhalten — auf „Erneut senden" klicken.')
        } else {
          setError('Verbindungsproblem. Die Frage bleibt erhalten — auf „Erneut senden" klicken.')
        }
        // TQ #6490: Message NICHT entfernen — als failed markieren (Retry-Button).
        setMessages(prev => {
          const lastUserIdx = [...prev]
            .reverse()
            .findIndex(m => m.role === 'user' && m.content === trimmed && !m.failed)
          if (lastUserIdx === -1) return prev
          const idx = prev.length - 1 - lastUserIdx
          return prev.map((m, i) => (i === idx ? { ...m, failed: true } : m))
        })
      } finally {
        setLoading(false)
        // TQ #7756: Focus nach Antwort-Ankunft wiederherstellen — User kann sofort
        // naechste Frage tippen ohne manuellen Klick.
        requestAnimationFrame(() => textareaRef.current?.focus())
      }
    },
    [loading, sessionId],
  )

  const handleSend = () => {
    const trimmed = input.trim()
    if (!trimmed || loading) return
    setInput('')
    void sendMessage(trimmed)
    // TQ #7756: rAF statt synchroner focus() — defer past React render + scrollIntoView
    // effects, die in Chromium (Edge+Brave) den Focus stehlen koennen.
    requestAnimationFrame(() => textareaRef.current?.focus())
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="scout-landing">
      {/* Left panel — branding + value props + auth CTAs */}
      <aside className="scout-landing-side">
        <div className="scout-landing-brand">
          <div className="scout-landing-logo">
            <svg width="48" height="48" viewBox="0 0 64 64" role="img" aria-label="IT-SCE Workspace">
              <rect width="64" height="64" rx="12" fill="#1a2235" />
              <text x="32" y="26" fontFamily="Arial, Helvetica, sans-serif" fontSize="13" fontWeight="700" fill="#FE4F00" textAnchor="middle" letterSpacing="0.5">IT-SCE</text>
              <rect x="14" y="30" width="36" height="2" rx="1" fill="#FE4F00" opacity="0.6" />
              <text x="32" y="48" fontFamily="Arial, Helvetica, sans-serif" fontSize="11" fontWeight="600" fill="#FFFFFF" textAnchor="middle" opacity="0.85">Workspace</text>
            </svg>
          </div>
          <h1 className="scout-landing-title">IT-SCE</h1>
          <p className="scout-landing-subtitle">Ihr KI-Mitarbeiter</p>
        </div>

        <div className="scout-landing-intro">
          <div className="scout-feature">
            <span className="scout-feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
                <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
              </svg>
            </span>
            <div>
              <strong>Aria – Ihr KI-Mitarbeiter</strong>
              <p>Aria arbeitet über Ihre Systeme — egal ob bei IT-SCE gehostet oder extern (Lexoffice, Clockin u.a. via API-Key).</p>
            </div>
          </div>
          <div className="scout-feature">
            <span className="scout-feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
                <path d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
              </svg>
            </span>
            <div>
              <strong>Beliebige Systeme anbinden</strong>
              <p>Nach dem Login bedient Aria Ihre Tools direkt im Chat — Mail, ERP, Dateien, Buchhaltung. Hosting bei IT-SCE macht die Anbindung enger, ist aber kein Muss.</p>
            </div>
          </div>
          <div className="scout-feature">
            <span className="scout-feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
                <path d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
            </span>
            <div>
              <strong>Datenschutz &amp; Sicherheit</strong>
              <p>Europäischer Datenschutz, Ende-zu-Ende verschlüsselt, DSGVO-konform.</p>
            </div>
          </div>
        </div>

        <div className="scout-landing-ctas">
          {/* TQ #6906 Patric 2026-06-03: Email-first/idp-resolve war Multi-Realm-Pattern.
              with.it-sce.de = Personal-Users Realm 1:1 → kein Discovery-Step nötig.
              Direkter login() zeigt sofort die KC-Login-Form des Personal-Users Realm.
              showEmailLogin/loginWithEmail/loginEmail State + useEmailFirstLogin hook
              werden hier nicht mehr aufgerufen, der Import bleibt fuer andere Pages. */}
          <button className="scout-btn-login" onClick={() => login()}>
            Anmelden
          </button>
          {/* TQ #7756: relative /registrieren (same-origin) statt absolutem
              registrierung.it-sce.de — die alte Domain wird via #6853 eliminiert
              und ihr 308-Redirect ist die einzige Bruecke; ohne ihn = weisses
              Fenster (BUG A). /registrieren rendert direkt das customer-portal
              Self-Register-Formular (AppRoot-Intercept). */}
          <a className="scout-btn-register" href="/registrieren">
            Jetzt registrieren
          </a>
        </div>

        <p className="scout-landing-footer">
          © 2026 IT-SCE •{' '}
          <a href="https://it-sce.de" target="_blank" rel="noopener noreferrer">
            it-sce.de
          </a>
        </p>
      </aside>

      {/* Right panel — Scout chat */}
      <section className="scout-chat-panel">
        <div className="scout-chat-header">
          <span className="scout-chat-logo" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 64 64" role="img" aria-label="IT-SCE Workspace">
              <rect width="64" height="64" rx="14" fill="#1a2235" />
              <text x="32" y="40" fontFamily="Arial, Helvetica, sans-serif" fontSize="17" fontWeight="700" fill="#FE4F00" textAnchor="middle" letterSpacing="0.5">IT-SCE</text>
            </svg>
          </span>
          <span className="scout-chat-title">
            <strong>IT-SCE</strong>
            <span className="scout-chat-subtitle">Scout · Ihr KI-Assistent</span>
          </span>
          <span className="scout-chat-badge">Kostenlos testen</span>
          {messages.length > 0 && (
            <button
              type="button"
              className="scout-chat-reset"
              onClick={handleNewChat}
              title="Neuen Chat starten"
            >
              Neuer Chat
            </button>
          )}
        </div>

        <div className="scout-chat-messages">
          {messages.length === 0 && !loading && (
            <div className="scout-chat-welcome">
              <p>Hallo! Ich bin Scout, Ihr IT-SCE Assistent.</p>
              <p>Stellen Sie mir eine Frage — ganz ohne Anmeldung:</p>
              <ul>
                <li>„Was kann Aria für mein Unternehmen tun?"</li>
                <li>„Kann Aria mein bestehendes Lexoffice/ERP anbinden?"</li>
                <li>„Was kostet der Workspace mit Aria?"</li>
              </ul>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`scout-msg scout-msg--${msg.role}${msg.failed ? ' scout-msg--failed' : ''}`}
            >
              <div className="scout-msg-avatar">
                {msg.role === 'user' ? 'Sie' : 'S'}
              </div>
              <div className="scout-msg-content">
                {msg.role === 'assistant'
                  ? <MarkdownMessage content={msg.content} />
                  : msg.content}
                {/* TQ #6490: fehlgeschlagene User-Message bleibt sichtbar + Retry */}
                {msg.failed && (
                  <div className="scout-msg-failed-row">
                    <span className="scout-msg-failed-hint">⚠ nicht zugestellt</span>
                    <button
                      type="button"
                      className="scout-retry-btn"
                      onClick={() => void sendMessage(msg.content)}
                      disabled={loading}
                    >
                      Erneut senden
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="scout-msg scout-msg--assistant">
              <div className="scout-msg-avatar">S</div>
              <div className="scout-msg-content scout-typing">
                <span className="scout-typing-label">Scout denkt nach…</span>
                <span className="scout-dot" />
                <span className="scout-dot" />
                <span className="scout-dot" />
              </div>
            </div>
          )}

          {error && (
            <div className="scout-chat-error">{error}</div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="scout-chat-input-area">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Frage an Scout… (Enter = Senden)"
            // TQ #7544: NICHT disabled waehrend loading — Nutzer kann parallel die
            // naechste Frage tippen. Senden bleibt via Guard (loading) serialisiert.
            rows={1}
            className="scout-textarea"
          />
          <button
            className="scout-send-btn"
            onClick={handleSend}
            disabled={loading || !input.trim()}
            title="Senden"
            aria-label="Nachricht senden"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="20" height="20">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            <span className="scout-send-label">Senden</span>
          </button>
        </div>

        <p className="scout-chat-hint">
          Für alle Funktionen:{' '}
          <button className="scout-chat-hint-login" onClick={() => login()}>
            Anmelden
          </button>{' '}
          oder{' '}
          <a className="scout-chat-hint-login" href="/registrieren">
            Konto erstellen
          </a>
        </p>
      </section>
    </div>
  )
}
